import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import CampaignDetail from '../pages/CampaignDetail';
import { useDataClock } from '../data';
import { useCampaignData } from '../data/queries/campaignData';
import RouteMessage from './RouteMessage';
import { campaignPath } from './paths';
import { useGoBack } from './useGoBack';

/**
 * `/campaigns/:id`. The id is a campaign's, or an incident's: an incident inside a campaign goes to that
 * campaign's own address, and a lone incident opens as a campaign of one. The records come from the shared
 * cache, so the screen opens from a link, a reload or the feed alike.
 */
const CampaignRoute: React.FC = () => {
  const { id = '' } = useParams();
  const goBack = useGoBack();
  const { view, loading, error } = useCampaignData(id);
  const { refreshNow } = useDataClock();
  const [retrying, setRetrying] = useState(false);

  // Try again reads through the one clock; the screen says it is loading meanwhile, so the click is never dead.
  const retry = () => {
    setRetrying(true);
    void refreshNow().finally(() => setRetrying(false));
  };

  if (view) {
    if (view.id !== id) return <Navigate to={campaignPath(view.id)} replace />;
    return <CampaignDetail view={view} onBack={goBack} />;
  }
  // While it reads again the title keeps its place (Back stays), and each state is its own node, so the alert is a new one.
  if (loading || retrying) {
    return <RouteMessage key="busy" busy title="Loading campaign…" onBack={retrying ? goBack : undefined} />;
  }
  if (error) {
    return (
      <RouteMessage
        key="failed"
        title="Could not load the campaign"
        detail="Nothing was changed. Try again in a moment."
        onBack={goBack}
        onRetry={retry}
      />
    );
  }
  return (
    <RouteMessage
      title="Campaign not found"
      detail={`There is no campaign or incident ${id} in this organization. It may belong to another organization or have been removed.`}
      onBack={goBack}
    />
  );
};

export default CampaignRoute;
